import { TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import theme from 'styles/theme';
import {
  EPartnerRequestLogAction,
  TPartnerRequestLogAction,
  EPartnerRequestStatus,
  TPartnerRequestStatus,
  ERequestType,
  TRequestType,
} from 'FeatureModules/Partner/types/partner.types';

type TRequestStatusType = {
  iconName: TIconName;
  statusTitle?: string;
  actionText?: string;
};

export const REQUEST_LOG_ACTION_TITLES: Record<TPartnerRequestLogAction, string> = {
  [EPartnerRequestLogAction.REQUESTED]: 'Requested',
  [EPartnerRequestLogAction.PROVIDED]: 'Provided',
  [EPartnerRequestLogAction.DENIED]: 'Denied',
  [EPartnerRequestLogAction.DELETED]: 'Deleted',
  [EPartnerRequestLogAction.EDITED]: 'Edited',
  [EPartnerRequestLogAction.COMPLETED]: 'Completed',
};

export const REQUEST_STATUS_COLOR_MAPPING: Record<TPartnerRequestStatus, string> = {
  [EPartnerRequestStatus.REQUESTED]: theme.custom.colors.textLight,
  [EPartnerRequestStatus.PROVIDED]: theme.palette.primary.main,
  [EPartnerRequestStatus.PROVIDED_COMPLETED]: theme.palette.success.main,
  [EPartnerRequestStatus.DENIED]: theme.palette.error.main,
  [EPartnerRequestStatus.DENIED_COMPLETED]: theme.palette.error.main,
  [EPartnerRequestStatus.DELETED]: theme.custom.colors.textLight,
  [EPartnerRequestStatus.EDITED]: theme.custom.colors.textLight,
};

/**
 * Depending on the requestType and PartnerRequestStatus the correct object is returned
 */

export const REQUEST_STATUS_CLIENT: Record<
  TRequestType,
  Record<TPartnerRequestStatus, TRequestStatusType>
> = {
  [ERequestType.DOCUMENT]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'document-request-uploaded',
      statusTitle: 'Document uploaded',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'document-request-fulfilled',
      statusTitle: 'Completed',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'document-request-pending',
      statusTitle: 'Pending',
    },
    [EPartnerRequestStatus.EDITED]: { iconName: 'document-request-manage', statusTitle: 'Pending' },
  },
  [ERequestType.CHAIN]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'chain-request-uploaded',
      statusTitle: 'Chain mapped',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'chain-request-fulfilled',
      statusTitle: 'Completed',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'chain-request-pending',
      statusTitle: 'Pending',
    },
    [EPartnerRequestStatus.EDITED]: { iconName: 'chain-request-manage', statusTitle: 'Pending' },
  },
  [ERequestType.QUESTIONNAIRE]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by partner',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'document-request-uploaded',
      statusTitle: 'Self-assessment provided',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'document-request-fulfilled',
      statusTitle: 'Completed',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'document-request-pending',
      statusTitle: 'Pending',
    },
    [EPartnerRequestStatus.EDITED]: {
      iconName: 'document-request-manage',
      statusTitle: 'Pending',
    },
  },
};

export const REQUEST_STATUS_PARTNER: Record<
  TRequestType,
  Record<EPartnerRequestStatus, TRequestStatusType>
> = {
  [ERequestType.DOCUMENT]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'document-request-uploaded',
      statusTitle: 'Document uploaded',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'document-request-fulfilled',
      statusTitle: 'Accepted',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'document-request-pending',
      statusTitle: 'Pending',
      actionText: 'Upload document',
    },
    [EPartnerRequestStatus.EDITED]: {
      iconName: 'document-request-manage',
      statusTitle: 'Pending',
      actionText: 'Upload document',
    },
  },
  [ERequestType.CHAIN]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'chain-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'chain-request-uploaded',
      statusTitle: 'Chain mapped',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'chain-request-fulfilled',
      statusTitle: 'Accepted',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'chain-request-pending',
      statusTitle: 'Pending',
      actionText: 'Map & confirm chain',
    },
    [EPartnerRequestStatus.EDITED]: {
      iconName: 'chain-request-manage',
      statusTitle: 'Pending',
      actionText: 'Map & confirm chain',
    },
  },
  [ERequestType.QUESTIONNAIRE]: {
    [EPartnerRequestStatus.DELETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Deleted',
    },
    [EPartnerRequestStatus.DENIED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.DENIED_COMPLETED]: {
      iconName: 'document-request-dismissed',
      statusTitle: 'Dismissed by you',
    },
    [EPartnerRequestStatus.PROVIDED]: {
      iconName: 'document-request-uploaded',
      statusTitle: 'Self-assessment answered',
    },
    [EPartnerRequestStatus.PROVIDED_COMPLETED]: {
      iconName: 'document-request-fulfilled',
      statusTitle: 'Accepted',
    },
    [EPartnerRequestStatus.REQUESTED]: {
      iconName: 'document-request-pending',
      statusTitle: 'Pending',
      actionText: 'Open self-assessment',
    },
    [EPartnerRequestStatus.EDITED]: {
      iconName: 'document-request-manage',
      statusTitle: 'Pending',
      actionText: 'Open self-assessment',
    },
  },
};
